/**
 * The CLEANR Bottle is an upsell-only companion to The CLEANR Sip.
 * If the Sip is not in the cart, every Bottle line is removed - including when
 * the Sip is removed and the Bottle would otherwise remain alone.
 *
 * NOTE: this is a UX layer only. Enforce the rule at checkout with a Shopify
 * Function (cart validation / cart transform), since JS can be bypassed.
 */
(() => {
  if (window.__cleanrBottleCompanion) return; // guard against double-loading
  window.__cleanrBottleCompanion = true;

  const SIP_PRODUCT_ID = 9633685897448;
  const BOTTLE_PRODUCT_ID = 15367581663464;
  const BASE_EVENT = 'bottle-companion';
  const LOG = '[cart-bottle-companion]';
  const MAX_PASSES = 3; // hard stop so we can never loop forever

  const docEl = document.documentElement;
  const routesRoot = () => window.Shopify?.routes?.root ?? '/';
  const emit = (name, detail) =>
    docEl.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));

  const hasItems = (cart) => Array.isArray(cart?.items);
  // Number() because some themes/apps hand us string IDs
  const isProduct = (item, id) => Number(item?.product_id) === id;
  const hasSip = (cart) => cart.items.some((i) => isProduct(i, SIP_PRODUCT_ID));
  const bottleKeys = (cart) =>
    cart.items.filter((i) => isProduct(i, BOTTLE_PRODUCT_ID)).map((i) => i.key);

  let syncing = false;
  let pending = false; // a cart change arrived while we were busy

  async function fetchCart() {
    const res = await fetch(`${routesRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store', // avoid stale cart from HTTP/bfcache
    });
    if (!res.ok) throw new Error(`cart.js responded ${res.status}`);
    return res.json();
  }

  /** Removes the given lines. Returns the updated cart payload, or null on failure/reload. */
  async function removeLines(keys) {
    emit('theme:loading:start');
    try {
      // Let the theme tell us which sections to bundle into the response
      const sections = new Set();
      emit('cart:prepare-bundled-sections', { sections });

      const body = { updates: Object.fromEntries(keys.map((k) => [k, 0])) };
      if (sections.size > 0) {
        body.sections = [...sections].join(',');
        body.sections_url = window.location.pathname;
      }

      const res = await fetch(`${routesRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const payload = await res.json().catch(() => null);
      if (!res.ok || !payload) {
        console.error(`${LOG} Failed to remove bottle lines`, res.status, payload);
        return null;
      }

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return null;
      }

      emit('cart:change', { baseEvent: BASE_EVENT, cart: payload });
      return payload;
    } catch (error) {
      console.error(LOG, error);
      return null;
    } finally {
      emit('theme:loading:end');
    }
  }

  /** Serialised sync: never drops an event, never runs two updates at once. */
  async function sync(cart) {
    if (syncing) {
      pending = true;
      return;
    }
    syncing = true;

    try {
      let current = cart;
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        pending = false;

        // Event payloads can be partial - fall back to the source of truth
        if (!hasItems(current)) current = await fetchCart();

        if (hasSip(current)) break;
        const keys = bottleKeys(current);
        if (keys.length === 0) break;

        const updated = await removeLines(keys);
        if (!updated) break;

        // If something changed while we were busy, re-verify against a fresh cart
        if (!pending) break;
        current = null;
      }
    } catch (error) {
      console.error(LOG, error);
    } finally {
      syncing = false;
      pending = false;
    }
  }

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.baseEvent === BASE_EVENT) return; // ignore our own event
    sync(event.detail?.cart);
  });

  const boot = () => sync(null);

  // Back/forward cache can restore a stale page where the cart has since changed
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) boot();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();